import React from 'react';
import type { SnackbarKey } from 'notistack';
import { Button } from '@ui/buttons';
import type { Project } from '@shared/models/project.model';
import type { IUpdateProjectOptions } from './project-state.model';

export type ApplyProjectChangesSnackbarTiming = 'beforeEnrich' | 'afterEnrich';

type ApplyProjectChangesSnackbarShared = {
  message: string;
  autoHideDuration?: number;
  /**
   * When to show the snackbar relative to the enrichment worker.
   * @defaultValue `'beforeEnrich'`
   */
  timing?: ApplyProjectChangesSnackbarTiming;
};

export type ApplyProjectChangesSnackbarOptions =
  | ({ type: 'success' } & ApplyProjectChangesSnackbarShared)
  | ({
      type: 'undo';
      undoLabel?: string;
      /** Invoked after the global undo action completes (e.g. restore URL selection). */
      onAfterUndo?: () => void | Promise<void>;
    } & ApplyProjectChangesSnackbarShared);

type EnrichOutcome = 'done' | 'failed';

type ApplyProjectUpdateLifecycle = Pick<
  IUpdateProjectOptions,
  | 'onOptimisticLocalUpdate'
  | 'onEnriched'
  | 'onEnrichFailed'
  | 'onEnrichmentSuperseded'
>;

/**
 * Runs caller lifecycle hooks and snackbar hooks in that order.
 * {@link useApplyProjectChanges} spreads snackbar callbacks last; without this, a snackbar
 * would replace {@link IUpdateProjectOptions.onOptimisticLocalUpdate} (e.g. version-add navigation).
 */
export const mergeApplyProjectUpdateLifecycle = (
  fromOptions: ApplyProjectUpdateLifecycle,
  fromSnackbar: ApplyProjectUpdateLifecycle,
): ApplyProjectUpdateLifecycle => ({
  onOptimisticLocalUpdate: () => {
    fromOptions.onOptimisticLocalUpdate?.();
    fromSnackbar.onOptimisticLocalUpdate?.();
  },
  onEnriched: () => {
    fromOptions.onEnriched?.();
    fromSnackbar.onEnriched?.();
  },
  onEnrichFailed: () => {
    fromOptions.onEnrichFailed?.();
    fromSnackbar.onEnrichFailed?.();
  },
  onEnrichmentSuperseded: () => {
    fromOptions.onEnrichmentSuperseded?.();
    fromSnackbar.onEnrichmentSuperseded?.();
  },
});

export type ApplyProjectChangesSnackbarEnqueue = (
  message: string,
  options: {
    autoHideDuration: number;
    variant?: 'success';
    action?: (snackbarId: SnackbarKey) => React.ReactNode;
  },
) => SnackbarKey;

/**
 * Builds enrichment-phase callbacks for a post-apply snackbar ({@link ApplyProjectChangesSnackbarOptions}).
 * For `undo`, Undo waits for a successful enrich when shown early — the undo stack only captures enriched transitions.
 *
 * @param params - Snackbar + optional undo wiring from the apply hook
 * @returns Callbacks to pass into {@link useUpdateProject}
 */
export const getApplyProjectChangesSnackbarCallbacks = (params: {
  snackbar: ApplyProjectChangesSnackbarOptions;
  enqueueSnackbar: ApplyProjectChangesSnackbarEnqueue;
  closeSnackbar: (key?: SnackbarKey) => void;
  undo: () => Promise<Project>;
  /** Waits until the temporal undo stack has the pre-change snapshot (filled after React commits the enriched project). */
  waitForUndoStack: () => Promise<void>;
}): Pick<
  IUpdateProjectOptions,
  | 'onOptimisticLocalUpdate'
  | 'onEnriched'
  | 'onEnrichFailed'
  | 'onEnrichmentSuperseded'
> => {
  const { snackbar, enqueueSnackbar, closeSnackbar, undo, waitForUndoStack } =
    params;
  const timing = snackbar.timing ?? 'beforeEnrich';
  let snackbarKey: SnackbarKey | undefined;

  const closeOwnSnackbar = () => {
    if (snackbarKey === undefined) return;
    closeSnackbar(snackbarKey);
  };

  if (snackbar.type === 'success') {
    const { message, autoHideDuration = 6000 } = snackbar;
    const show = () => {
      snackbarKey = enqueueSnackbar(message, {
        variant: 'success',
        autoHideDuration,
      });
    };
    return {
      onOptimisticLocalUpdate: timing === 'beforeEnrich' ? show : undefined,
      onEnriched: () => {
        if (timing === 'afterEnrich') show();
      },
      onEnrichFailed: closeOwnSnackbar,
      onEnrichmentSuperseded: closeOwnSnackbar,
    };
  }

  const {
    message,
    undoLabel = 'Undo',
    autoHideDuration = 8000,
    onAfterUndo,
  } = snackbar;

  let outcome: 'pending' | EnrichOutcome = 'pending';
  const outcomeWaiters: Array<(o: EnrichOutcome) => void> = [];

  const settleEnrichOutcome = (o: EnrichOutcome) => {
    if (outcome !== 'pending') return;
    outcome = o;
    while (outcomeWaiters.length) {
      const w = outcomeWaiters.shift();
      w?.(o);
    }
  };

  const waitForEnrichOutcome = () =>
    outcome === 'pending'
      ? new Promise<EnrichOutcome>((resolve) => {
          outcomeWaiters.push(resolve);
        })
      : Promise.resolve(outcome);

  const runUndo = async (snackbarId: SnackbarKey) => {
    if (timing === 'beforeEnrich') {
      const o = await waitForEnrichOutcome();
      if (o === 'failed') return;
    }
    await waitForUndoStack();
    await undo();
    await onAfterUndo?.();
    closeSnackbar(snackbarId);
  };

  const showUndoSnackbar = () => {
    snackbarKey = enqueueSnackbar(message, {
      autoHideDuration,
      action: (snackbarId) =>
        React.createElement(Button, {
          variant: 'text',
          size: 'small',
          // Keep an open inline rename (e.g. new-version name) from committing on blur
          'data-keep-inline-edit': true,
          onClick: () => {
            void runUndo(snackbarId);
          },
          children: undoLabel,
        }),
    });
  };

  return {
    onOptimisticLocalUpdate:
      timing === 'beforeEnrich' ? () => showUndoSnackbar() : undefined,
    onEnriched: () => {
      settleEnrichOutcome('done');
      if (timing === 'afterEnrich') showUndoSnackbar();
    },
    onEnrichFailed: () => {
      settleEnrichOutcome('failed');
      closeOwnSnackbar();
    },
    onEnrichmentSuperseded: () => {
      settleEnrichOutcome('failed');
      closeOwnSnackbar();
    },
  };
};
